import type { ColorSpec, ColorTransform, Rgba, SchemeColorName } from './color.js';
import { normaliseHex } from './color.js';
import { milliPercentToFraction } from './units.js';

/**
 * The colour map on a master/layout/slide redirects the four "slide-relative"
 * names onto real theme slots. `tx1 -> dk1` is the identity mapping, but a slide
 * with an inverted background swaps them, and text silently comes out white on
 * white if this indirection is skipped.
 */
export type ColorMap = Readonly<Record<'bg1' | 'tx1' | 'bg2' | 'tx2', SchemeColorName>>;

export const DEFAULT_COLOR_MAP: ColorMap = {
  bg1: 'lt1',
  tx1: 'dk1',
  bg2: 'lt2',
  tx2: 'dk2',
};

export type ColorSchemeSlot =
  | 'dk1'
  | 'lt1'
  | 'dk2'
  | 'lt2'
  | 'accent1'
  | 'accent2'
  | 'accent3'
  | 'accent4'
  | 'accent5'
  | 'accent6'
  | 'hlink'
  | 'folHlink';

export type ColorScheme = Readonly<Record<ColorSchemeSlot, string>>;

export interface ColorContext {
  readonly scheme: ColorScheme;
  readonly colorMap: ColorMap;
  /** Substituted for `phClr` when resolving a style-matrix reference. */
  readonly placeholderColor?: Rgba;
}

const PRESET_COLORS: Readonly<Record<string, string>> = {
  black: '000000',
  blue: '0000FF',
  cyan: '00FFFF',
  darkGray: 'A9A9A9',
  gray: '808080',
  green: '008000',
  lightGray: 'D3D3D3',
  magenta: 'FF00FF',
  orange: 'FFA500',
  purple: '800080',
  red: 'FF0000',
  white: 'FFFFFF',
  yellow: 'FFFF00',
};

const SYSTEM_COLORS: Readonly<Record<string, string>> = {
  windowText: '000000',
  window: 'FFFFFF',
  highlight: '0078D4',
  highlightText: 'FFFFFF',
  btnFace: 'F0F0F0',
  btnText: '000000',
};

/** Resolves a `ColorSpec` to concrete RGBA, replaying colour transforms in order. */
export function resolveColor(spec: ColorSpec, ctx: ColorContext): Rgba {
  let base = baseColor(spec, ctx);
  for (const t of spec.transforms) {
    base = applyTransform(base, t);
  }
  return base;
}

function baseColor(spec: ColorSpec, ctx: ColorContext): Rgba {
  switch (spec.kind) {
    case 'srgb':
      return hexToRgba(spec.hex);
    case 'scheme':
      return hexToRgba(resolveSchemeHex(spec.name, ctx));
    case 'preset':
      return hexToRgba(PRESET_COLORS[spec.name] ?? '000000');
    case 'system':
      return hexToRgba(spec.lastHex ?? SYSTEM_COLORS[spec.name] ?? '000000');
    case 'scrgb':
      return {
        r: linearToSrgbByte(spec.r / 100000),
        g: linearToSrgbByte(spec.g / 100000),
        b: linearToSrgbByte(spec.b / 100000),
        a: 1,
      };
  }
}

function resolveSchemeHex(name: SchemeColorName, ctx: ColorContext): string {
  if (name === 'phClr') {
    const ph = ctx.placeholderColor;
    return ph ? rgbaToHex(ph) : '000000';
  }
  if (name === 'bg1' || name === 'tx1' || name === 'bg2' || name === 'tx2') {
    return ctx.scheme[ctx.colorMap[name] as ColorSchemeSlot] ?? '000000';
  }
  return ctx.scheme[name as ColorSchemeSlot] ?? '000000';
}

function applyTransform(c: Rgba, t: ColorTransform): Rgba {
  const v = t.value === undefined ? undefined : milliPercentToFraction(t.value);
  switch (t.op) {
    case 'alpha':
      return { ...c, a: v ?? c.a };
    case 'alphaMod':
      return { ...c, a: clamp01(c.a * (v ?? 1)) };
    case 'alphaOff':
      return { ...c, a: clamp01(c.a + (v ?? 0)) };
    case 'shade':
      return shade(c, v ?? 1);
    case 'tint':
      return tint(c, v ?? 1);
    case 'lumMod':
      return withHsl(c, (h, s, l) => [h, s, clamp01(l * (v ?? 1))]);
    case 'lumOff':
      return withHsl(c, (h, s, l) => [h, s, clamp01(l + (v ?? 0))]);
    case 'satMod':
      return withHsl(c, (h, s, l) => [h, clamp01(s * (v ?? 1)), l]);
    case 'satOff':
      return withHsl(c, (h, s, l) => [h, clamp01(s + (v ?? 0)), l]);
    case 'hueMod':
      return withHsl(c, (h, s, l) => [wrapHue(h * (v ?? 1)), s, l]);
    case 'hueOff':
      // hueOff carries an angle in 60000ths of a degree, not a percentage.
      return withHsl(c, (h, s, l) => [wrapHue(h + (t.value ?? 0) / 60000), s, l]);
    case 'gray': {
      const y = 0.299 * c.r + 0.587 * c.g + 0.114 * c.b;
      return { r: y, g: y, b: y, a: c.a };
    }
    case 'inv':
      return { r: 255 - c.r, g: 255 - c.g, b: 255 - c.b, a: c.a };
    case 'comp':
      return withHsl(c, (h, s, l) => [wrapHue(h + 180), s, l]);
  }
}

/**
 * `shade` and `tint` are defined in linear RGB, not sRGB. Applying them to the
 * gamma-encoded bytes produces visibly wrong tones for every theme-derived fill,
 * which is a common reason a published deck looks washed out next to PowerPoint.
 */
function shade(c: Rgba, factor: number): Rgba {
  return mapLinear(c, (x) => x * factor);
}

function tint(c: Rgba, factor: number): Rgba {
  return mapLinear(c, (x) => x * factor + (1 - factor));
}

function mapLinear(c: Rgba, f: (x: number) => number): Rgba {
  return {
    r: linearToSrgbByte(f(srgbByteToLinear(c.r))),
    g: linearToSrgbByte(f(srgbByteToLinear(c.g))),
    b: linearToSrgbByte(f(srgbByteToLinear(c.b))),
    a: c.a,
  };
}

function srgbByteToLinear(byte: number): number {
  const x = byte / 255;
  return x <= 0.04045 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
}

function linearToSrgbByte(linear: number): number {
  const x = clamp01(linear);
  const encoded = x <= 0.0031308 ? x * 12.92 : 1.055 * x ** (1 / 2.4) - 0.055;
  return Math.round(clamp01(encoded) * 255);
}

function withHsl(c: Rgba, f: (h: number, s: number, l: number) => [number, number, number]): Rgba {
  const [h, s, l] = rgbToHsl(c);
  const [h2, s2, l2] = f(h, s, l);
  return { ...hslToRgb(h2, s2, l2), a: c.a };
}

export function hexToRgba(hex: string): Rgba {
  const h = normaliseHex(hex);
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
    a: 1,
  };
}

export function rgbaToHex(c: Rgba): string {
  return [c.r, c.g, c.b]
    .map((x) =>
      Math.max(0, Math.min(255, Math.round(x)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')
    .toUpperCase();
}

function rgbToHsl({ r, g, b }: Rgba): [number, number, number] {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60;
  else if (max === gn) h = ((bn - rn) / d + 2) * 60;
  else h = ((rn - gn) / d + 4) * 60;
  return [h, s, l];
}

function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  if (s === 0) {
    const v = Math.round(l * 255);
    return { r: v, g: v, b: v };
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hk = wrapHue(h) / 360;
  return {
    r: Math.round(hueToChannel(p, q, hk + 1 / 3) * 255),
    g: Math.round(hueToChannel(p, q, hk) * 255),
    b: Math.round(hueToChannel(p, q, hk - 1 / 3) * 255),
  };
}

function hueToChannel(p: number, q: number, t: number): number {
  let x = t;
  if (x < 0) x += 1;
  if (x > 1) x -= 1;
  if (x < 1 / 6) return p + (q - p) * 6 * x;
  if (x < 1 / 2) return q;
  if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
  return p;
}

function wrapHue(h: number): number {
  return ((h % 360) + 360) % 360;
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}
